import * as React from "react";

import {
  ChartForm as ChartFormType,
  ChartRouteBaseParams,
  ChartRow,
  ChartRowElement,
  ChartRowElementImportedProps,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
  mapFormToPayload,
  mapPayloadToFormObject,
  useChartApplication,
  useSaveAndNext,
  useUpdateChartApplication,
} from "modules";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Icon, useToast } from "@chakra-ui/react";
import { useChartTrackingContext, usePermissions } from "contexts";

import { ChartForm } from "modules/charts-shared-v2";
import { EmrClinicalImpression } from "@medstonetech/slate-icons";
import { FormPromptWithSaveProps } from "shared/prompt";
import { Loading } from "shared";
import { extractApiErrorMessage } from "utils";
import { formMessages } from "messages";
import { useParams } from "react-router-dom";

const sectionId = "SCLI";
const chartCode = "C014x";

function T14ClinicalImpression() {
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();
  const useFormContext = useForm<ChartFormType>();
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useFormContext;

  const { scope } = usePermissions();
  const isReadOnly = !scope("provider:ci").isEditable;

  const {
    data,
    isLoading: getIsLoading,
    error: getError,
  } = useChartApplication({
    chartCode,
    encounterId,
    sectionId,
  });
  const { mutateAsync: updateChartApplication, isLoading: updateIsLoading } =
    useUpdateChartApplication({
      chartCode,
      encounterId,
      sectionId,
    });
  const toast = useToast();
  const formObject = getValues();

  const { trackChartInteractions, isLoading: isTrackingLoading } =
    useChartTrackingContext();

  const onSubmit: SubmitHandler<ChartFormType> = React.useCallback(
    async (values) => {
      try {
        const payload = mapFormToPayload(values);
        await updateChartApplication({
          input: payload,
          chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
        });

        await trackChartInteractions(
          data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
          payload,
          {
            encounterId,
            chartCode,
            sectionId,
            chartType: "application",
          }
        );

        // Reset form with latest values to set isDirty to false
        reset(values);
        toast({
          description: formMessages.updateSuccess("Clinical Impression"),
        });
      } catch (error) {
        toast({ description: extractApiErrorMessage(error) });
      }
    },
    [
      updateChartApplication,
      trackChartInteractions,
      data?.data,
      encounterId,
      reset,
      toast,
      getValues,
    ]
  );

  const handleSaveAndNext = useSaveAndNext(
    handleSubmit(onSubmit),
    "clinical-impression",
    "provider",
    isDirty
  );

  const onRouteChangeSave: FormPromptWithSaveProps["onSave"] = async (
    onComplete
  ) => {
    try {
      const payload = mapFormToPayload(formObject);
      await updateChartApplication({
        input: payload,
        chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
      });
      await trackChartInteractions(
        data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
        payload,
        {
          encounterId,
          chartCode,
          sectionId,
          chartType: "application",
        }
      );

      toast({ description: formMessages.updateSuccess("Clinical Impression") });
      onComplete();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  React.useEffect(() => {
    if (data) {
      const form = mapPayloadToFormObject(data.data);
      reset(form);
    }
  }, [data, reset]);

  React.useEffect(() => {
    if (getError) {
      toast({ description: extractApiErrorMessage(getError) });
    }
  }, [getError, toast]);

  if (getIsLoading) {
    return <Loading />;
  }

  const chartFavoriteEditMode: Record<
    "imported",
    ChartRowElementImportedProps
  > | null = getValues("chartFavoriteEditMode")
    ? {
        imported: {
          type: "nursing",
        },
      }
    : null;

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={handleSubmit(onSubmit)}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={updateIsLoading || isTrackingLoading}
        disabled={isReadOnly}
        sectionId={sectionId}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrClinicalImpression} />}
          chartId={chartCode}
          encounterId={encounterId}
          sectionId={sectionId}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={handleSaveAndNext}
          showFavoritesBtn={!isReadOnly}
        >
          Clinical Impression {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Initial visit unless marked:"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q001.${chartCode}${sectionId}Q001A001`}
                />
                <ChartRowElement
                  label="subsequent"
                  name={`Q001.${chartCode}${sectionId}Q001A002`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="sequelae"
                  name={`Q001.${chartCode}${sectionId}Q001A003`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement type="empty" />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q002"
              subsectionLabel="ENT / Resp"
            >
              ENT / Resp
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Asthma:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A001`}
                />
                <ChartRowElement
                  label="Reactive airway dz:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A002`}
                />
                <ChartRowElement
                  label="acute exacerbation:"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A003`}
                />
                <ChartRowElement
                  label="status asthmaticus"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A004`}
                />
                <ChartRowElement
                  label="hx of:"
                  fontStyle="italic"
                  textDecoration="underline"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A005`}
                />
                <ChartRowElement
                  label="mild"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A006`}
                />
                <ChartRowElement
                  label="mod"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A007`}
                />
                <ChartRowElement
                  label="severe dz"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A008`}
                />
                <ChartRowElement
                  label="intermittent"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A009`}
                />
                <ChartRowElement
                  label="persistent"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A010`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A011`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Bronchospasm, acute"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A012`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A013`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Bronchitis:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A014`}
                />
                <ChartRowElement
                  label="acute"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A015`}
                />
                <ChartRowElement
                  label="RSV"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A016`}
                />
                <ChartRowElement
                  label="simple"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A017`}
                />
                <ChartRowElement
                  label="mucopurulent"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A018`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A019`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Cold (Nasopharyngitis)"
                  name={`Q002.${chartCode}${sectionId}Q002A020`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A021`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Croup"
                  name={`Q002.${chartCode}${sectionId}Q002A022`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A023`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="COVID-19:"
                  name={`Q002.${chartCode}${sectionId}Q002A024`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="disease"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A025`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="exposure"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A026`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A027`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Epiglottis:"
                  name={`Q002.${chartCode}${sectionId}Q002A028`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="acute"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A029`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="w/ obstruction"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A030`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A031`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Influenza:"
                  name={`Q002.${chartCode}${sectionId}Q002A032`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="A"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A033`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="B"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A034`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="A & B"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A035`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A036`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>

              <ChartRow>
                <ChartRowElement
                  label="Otitis media:"
                  name={`Q002.${chartCode}${sectionId}Q002A037`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="L"
                  name={`Q002.${chartCode}${sectionId}Q002A038`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="R"
                  name={`Q002.${chartCode}${sectionId}Q002A039`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="acute:"
                  name={`Q002.${chartCode}${sectionId}Q002A040`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="recurrent:"
                  name={`Q002.${chartCode}${sectionId}Q002A041`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="serous:"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A042`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="suppurative"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A043`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="w/ TM perf:"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A044`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="central"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A045`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="marginal"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A046`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A047`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Pharyngitis:"
                  name={`Q002.${chartCode}${sectionId}Q002A048`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="acute"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A049`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="Strep"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A050`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A051`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Sinusitis:"
                  name={`Q002.${chartCode}${sectionId}Q002A052`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="acute"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A053`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="recurrent"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A054`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="maxillary"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A055`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="frontal"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A056`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="ethmoid"
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A057`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A058`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="URI, acute"
                  name={`Q002.${chartCode}${sectionId}Q002A059`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A060`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A061`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q003"
              subsectionLabel="Other"
            >
              Other
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Dehydration"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A001`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A002`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Gastroenteritis"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A003`}
                />
                <ChartRowElement
                  label="infectious"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A004`}
                />
                <ChartRowElement
                  label="viral"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A005`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Kawasaki's disease"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A007`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A008`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Meningitis:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A009`}
                />
                <ChartRowElement
                  label="viral"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A010`}
                />
                <ChartRowElement
                  label="bacterial"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A011`}
                />
                <ChartRowElement
                  label="H influ"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A012`}
                />
                <ChartRowElement
                  label="Pneumo"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A013`}
                />
                <ChartRowElement
                  label="Strep"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A014`}
                />
                <ChartRowElement
                  label="Staph"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A015`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A016`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Mononucleosis:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A017`}
                />
                <ChartRowElement
                  label="EBV"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A018`}
                />
                <ChartRowElement
                  label="CMV"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A019`}
                />
                <ChartRowElement
                  label="w/:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  textDecoration="underline"
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A020`}
                />
                <ChartRowElement
                  label="splenomegaly"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A021`}
                />
                <ChartRowElement
                  label="hepatomegaly"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A022`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A023`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Nenonatal jaundice"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A024`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A025`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Poisoning, accidental (substance):"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A026`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A027`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Pylonephritis:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A028`}
                />
                <ChartRowElement
                  label="acute"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A029`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A030`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="SIRS"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A031`}
                />
                <ChartRowElement
                  label="Sepsis, severe"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A032`}
                />
                <ChartRowElement
                  label="w/:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  textDecoration="underline"
                  name={`Q003.${chartCode}${sectionId}Q003A033`}
                />
                <ChartRowElement
                  label="shock"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A034`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A035`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Skin rash:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A036`}
                />
                <ChartRowElement
                  label="impetigo:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A037`}
                />
                <ChartRowElement
                  label="nonbullus"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A038`}
                />
                <ChartRowElement
                  label="diaper rash"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A039`}
                />
                <ChartRowElement
                  label="varicella"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A040`}
                />
                <ChartRowElement
                  label="roseola"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A041`}
                />
                <ChartRowElement
                  label="fifth dz"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A042`}
                />
                <ChartRowElement
                  label="pityriasis rosea"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A043`}
                />
                <ChartRowElement
                  label="hand, foot, mouth dz"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A044`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A045`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Tick borne dz:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A046`}
                />
                <ChartRowElement
                  label="Rocky Mtn spotted fever"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A047`}
                />
                <ChartRowElement
                  label="Lyme disease"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A048`}
                />
                <ChartRowElement
                  label="Tick paralysis"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q003.${chartCode}${sectionId}Q003A049`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A050`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="UTI:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A051`}
                />
                <ChartRowElement
                  label="cystisis:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A052`}
                />
                <ChartRowElement
                  label="acute:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A053`}
                />
                <ChartRowElement
                  label="w/ hematuria:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q003.${chartCode}${sectionId}Q003A054`}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A055`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A056`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q004"
              subsectionLabel="Other"
            >
              Other
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Abd Pain"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A001`}
                />
                <ChartRowElement
                  label="LUQ"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A002`}
                />
                <ChartRowElement
                  label="RUQ"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A003`}
                />
                <ChartRowElement
                  label="LLQ"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A004`}
                />
                <ChartRowElement
                  label="RLQ"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A005`}
                />
                <ChartRowElement
                  label="generalized"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A006`}
                />
                <ChartRowElement
                  label="acute abd"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A007`}
                />
                <ChartRowElement
                  label="w/:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  textDecoration="underline"
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A008`}
                />
                <ChartRowElement
                  label="rebound"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A009`}
                />
                <ChartRowElement
                  label="tenderness"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q004.${chartCode}${sectionId}Q004A010`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A011`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Nausea"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A012`}
                />
                <ChartRowElement
                  label="Vomiting"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A013`}
                />
                <ChartRowElement
                  label="Diarrhea"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A014`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A015`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Fever"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A016`}
                />
                <ChartRowElement
                  label="Headache"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A017`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A018`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Hypoxema"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A019`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A020`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Hypokalemia"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A021`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A022`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Leukocytosis"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A023`}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A024`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartForm>
    </FormProvider>
  );
}

export { T14ClinicalImpression };
